"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface FormData {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}

const services = [
  "Litigio Civil y Comercial",
  "Derecho Corporativo",
  "Derecho de Familia",
  "Bienes Raíces",
  "Derecho Sucesorio",
  "Derecho Laboral",
];

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setSubmitStatus("success");
    setFormData({ name: "", email: "", phone: "", service: "", message: "" });

    setTimeout(() => setSubmitStatus("idle"), 5000);
  };

  return (
    <div>
      <h2 className="text-3xl text-primary font-serif mb-6">
        Envíenos un Mensaje
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
            Nombre Completo <span className="text-error">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 rounded-lg border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            placeholder="Juan Pérez"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            Correo Electrónico <span className="text-error">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 rounded-lg border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            placeholder="juan.perez@ejemplo.com"
          />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
            Teléfono
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            placeholder="+507 1234-5678"
          />
        </div>

        {/* Service */}
        <div>
          <label htmlFor="service" className="block text-sm font-medium text-foreground mb-2">
            Área de Interés <span className="text-error">*</span>
          </label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 rounded-lg border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
          >
            <option value="">Seleccione un área</option>
            {services.map((service) => (
              <option key={service} value={service}>
                {service}
              </option>
            ))}
          </select>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
            Mensaje <span className="text-error">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={6}
            className="w-full px-4 py-3 rounded-lg border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all resize-none"
            placeholder="Describa brevemente su consulta..."
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground py-4 px-6 rounded-full font-semibold text-sm uppercase tracking-wider transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              Enviar Consulta
              <Icon name="PaperAirplaneIcon" size={16} />
            </>
          )}
        </button>

        {/* Success Message */}
        {submitStatus === "success" && (
          <div className="bg-success/10 border border-success/20 text-success px-4 py-3 rounded-lg text-sm flex items-center gap-3">
            <Icon name="CheckCircleIcon" size={20} />
            <span>¡Gracias! Su mensaje ha sido enviado. Nos pondremos en contacto pronto.</span>
          </div>
        )}
      </form>

      {/* Alternative CTA */}
      <div className="mt-12 pt-12 border-t border-border">
        <h3 className="text-xl text-primary font-serif mb-4">
          ¿Prefiere hablar directamente?
        </h3>
        <p className="text-muted-foreground text-sm mb-6">
          Llámenos o programe una reunión virtual a través de nuestro calendario.
        </p>
        <button className="inline-flex items-center gap-3 bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-full text-sm font-medium transition-all">
          <Icon name="CalendarIcon" size={16} />
          Agendar Reunión Virtual
        </button>
      </div>
    </div>
  );
}